/** Highlights the selected step in the product explanation. */
class HomeBuildSteps extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController()
    this.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return
        const button = event.target.closest('[data-build-select]')
        if (!button) return
        for (const candidate of this.querySelectorAll('[data-build-select]')) {
          candidate.setAttribute('aria-pressed', String(candidate === button))
        }
        this.dataset.activeStep = button.dataset.buildSelect
      },
      { signal: this.controller.signal },
    )
  }

  disconnectedCallback() {
    this.controller.abort()
  }
}

/** Runs the two community rows while respecting pause and reduced motion. */
class HomeCommunityCarousel extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController()
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.paused = this.motion.matches || this.dataset.autoplay !== 'true'
    this.toggle = this.querySelector('[data-carousel-toggle]')
    for (const track of this.querySelectorAll('.home-community__track')) {
      for (const card of Array.from(track.children)) {
        const clone = card.cloneNode(true)
        clone.setAttribute('data-carousel-clone', '')
        clone.setAttribute('aria-hidden', 'true')
        clone.removeAttribute('data-shopify-editor-block')
        clone.inert = true
        track.append(clone)
      }
    }
    const options = { signal: this.controller.signal }
    this.toggle.addEventListener(
      'click',
      () => {
        this.paused = !this.paused
        this.updatePlayback()
      },
      options,
    )
    this.motion.addEventListener(
      'change',
      () => {
        this.paused = this.motion.matches || this.dataset.autoplay !== 'true'
        this.updatePlayback()
      },
      options,
    )
    document.addEventListener('visibilitychange', () => this.updatePlayback(), options)
    this.updatePlayback()
  }

  updatePlayback() {
    this.dataset.paused = String(this.paused || document.hidden)
    this.toggle.setAttribute('aria-pressed', String(this.paused))
    this.toggle.setAttribute(
      'aria-label',
      this.paused ? this.dataset.playLabel : this.dataset.pauseLabel,
    )
    this.toggle.firstElementChild.textContent = this.paused ? '▶' : 'Ⅱ'
  }

  disconnectedCallback() {
    this.controller.abort()
    for (const clone of this.querySelectorAll('[data-carousel-clone]')) clone.remove()
  }
}

if (!customElements.get('home-build-steps'))
  customElements.define('home-build-steps', HomeBuildSteps)
if (!customElements.get('home-community-carousel'))
  customElements.define('home-community-carousel', HomeCommunityCarousel)

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return
  const navigationLink = event.target.closest('.home-navigation a')
  const drawer = navigationLink?.closest('header-drawer')
  if (drawer) drawer.closeMenuDrawer(event, drawer.querySelector('summary'))
  const link = event.target.closest('[data-home-create]')
  if (!link) return
  const generator = document.querySelector('ai-personalization-generator')
  if (!generator) return
  event.preventDefault()
  generator.scrollIntoView({
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    block: 'start',
  })
  const input = generator.querySelector('input[type="file"]:not(:disabled)')
  if (input && generator.dataset.entryLayout === 'true') input.focus({ preventScroll: true })
})

/** The compact category bar opens the existing accessible header drawer. */
class MobileCategoryNavigation extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController()
    const options = { signal: this.controller.signal }
    const button = this.querySelector('button')
    const drawer = document.getElementById('Details-menu-drawer-container')
    if (!button || !drawer) return
    button.addEventListener('click', () => drawer.querySelector('summary').click(), options)
    drawer.addEventListener('toggle', () => {
      button.setAttribute('aria-expanded', String(drawer.open))
    }, options)
  }
  disconnectedCallback() { this.controller.abort() }
}
customElements.define('mobile-category-navigation', MobileCategoryNavigation)

/** Continuously scrolls the mobile claims; native scrolling remains available when paused. */
class HomeTrustCarousel extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController()
    this.track = this.querySelector('.home-trust__grid')
    this.items = Array.from(this.track.children)
    this.mobile = window.matchMedia('(max-width: 749px)')
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.visible = false
    this.interacting = false
    for (const item of this.items) {
      const clone = item.cloneNode(true)
      clone.removeAttribute('id')
      clone.removeAttribute('data-shopify-editor-block')
      clone.setAttribute('data-trust-clone', '')
      clone.setAttribute('aria-hidden', 'true')
      clone.inert = true
      this.track.append(clone)
    }
    const options = { signal: this.controller.signal }
    const update = () => this.updatePlayback()
    this.mobile.addEventListener('change', () => { this.track.scrollLeft = 0; update() }, options)
    this.motion.addEventListener('change', update, options)
    document.addEventListener('visibilitychange', update, options)
    this.addEventListener('focusin', update, options)
    this.addEventListener('focusout', () => queueMicrotask(update), options)
    this.addEventListener('pointerenter', event => {
      if (event.pointerType === 'mouse') { this.interacting = true; update() }
    }, options)
    this.addEventListener('pointerdown', () => { this.interacting = true; update() }, options)
    for (const event of ['pointerleave', 'pointerup', 'pointercancel']) {
      this.addEventListener(event, () => { this.interacting = false; update() }, options)
    }
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting
      update()
    }, { threshold: 0.5 })
    this.observer.observe(this)
    // Match the design's first mobile group: turnaround, rating, recycling.
    if (this.mobile.matches && this.items.length) {
      const start = this.items.findIndex(item => item.dataset.icon === 'clock')
      this.track.scrollLeft = this.itemOffset(Math.max(0, start))
    }
  }

  itemGap() {
    return Number.parseFloat(getComputedStyle(this.track).columnGap) || 0
  }

  itemOffset(index) {
    const gap = this.itemGap()
    return this.items.slice(0, index).reduce((offset, item) => offset + item.getBoundingClientRect().width + gap, 0)
  }

  cycleWidth() {
    return this.items.reduce((width, item) => width + item.getBoundingClientRect().width, 0) + this.itemGap() * this.items.length
  }

  updatePlayback() {
    cancelAnimationFrame(this.frame)
    this.lastTime = null
    if (!this.visible || !this.mobile.matches || this.motion.matches || document.hidden ||
        this.interacting || this.contains(document.activeElement) || this.items.length <= 3) return
    this.position = this.track.scrollLeft
    const advance = time => {
      const cycle = this.cycleWidth()
      if (this.lastTime !== null && cycle > 0) {
        // 18px/second, independent of display refresh rate, with a seamless repeated row.
        this.position = (this.position + Math.min(time - this.lastTime, 50) * 0.018) % cycle
        this.track.scrollLeft = this.position
      }
      this.lastTime = time
      this.frame = requestAnimationFrame(advance)
    }
    this.frame = requestAnimationFrame(advance)
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame)
    this.controller.abort()
    this.observer.disconnect()
    for (const clone of this.querySelectorAll('[data-trust-clone]')) clone.remove()
  }
}
if (!customElements.get('home-trust-carousel'))
  customElements.define('home-trust-carousel', HomeTrustCarousel)

/** Native review scrolling; repeated end cards keep mobile neighbours visible. */
class HomeReviewCarousel extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController()
    this.track = this.querySelector('[role="list"]')
    this.cards = Array.from(this.track.children)
    this.mobile = window.matchMedia('(max-width: 749px)')
    const options = { signal: this.controller.signal }
    if (this.cards.length > 1) {
      for (const [card, prepend] of [[this.cards.at(-1), true], [this.cards[0], false]]) {
        const clone = card.cloneNode(true)
        clone.removeAttribute('data-shopify-editor-block')
        clone.setAttribute('data-review-clone', '')
        clone.setAttribute('aria-hidden', 'true')
        clone.inert = true
        if (prepend) this.track.prepend(clone)
        else this.track.append(clone)
      }
    }
    const reset = () => {
      if (this.cards.length > 1) {
        for (const card of this.cards) this.track.insertBefore(card, this.track.lastElementChild)
      }
      this.track.scrollLeft = this.mobile.matches ? this.step : 0
    }
    this.mobile.addEventListener('change', reset, options)
    reset()
    this.addEventListener('click', event => {
      const button = event.target.closest('[data-review-direction]')
      if (button) this.advance(Number(button.dataset.reviewDirection))
    }, options)
    this.track.addEventListener('keydown', event => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault()
        this.advance(event.key === 'ArrowRight' ? 1 : -1)
      }
    }, options)
    this.track.addEventListener('scrollend', () => {
      if (!this.mobile.matches || this.cards.length < 2) return
      const index = Math.round(this.track.scrollLeft / this.step)
      if (index === 0) this.track.scrollLeft = this.cards.length * this.step
      else if (index === this.cards.length + 1) this.track.scrollLeft = this.step
    }, options)
  }
  get step() {
    return this.cards.length > 1 ? this.cards[0].getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap) : 0
  }
  advance(direction) {
    if (this.cards.length < 2) return
    if (!this.mobile.matches) {
      const cards = Array.from(this.track.children).filter(card => !card.hasAttribute('data-review-clone'))
      if (direction > 0) this.track.insertBefore(cards[0], this.track.lastElementChild)
      else this.track.insertBefore(cards.at(-1), cards[0])
      return
    }
    const next = Math.round(this.track.scrollLeft / this.step) + direction
    this.track.scrollTo({ left: next * this.step,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })
  }
  disconnectedCallback() {
    this.controller.abort()
    for (const clone of this.querySelectorAll('[data-review-clone]')) clone.remove()
  }
}
if (!customElements.get('home-review-carousel'))
  customElements.define('home-review-carousel', HomeReviewCarousel)
